import { View, Text, StyleSheet, ScrollView, Pressable, TouchableOpacity, Alert, FlatList } from 'react-native';
import React, { useState, useEffect } from 'react';
import { hp, wp } from '../../helpers/common';
import { useAuth } from '../../Utils/AuthProvider'
import { theme } from '../../constants/theme';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import Header from '../../Components/Header';
import ScreenWrapper from '../../Components/ScreenWrapper';
import {Edit,Mail,Logout}from '../../../assets/icons'
import Avatar from '../../Components/Avatar';
import FullScreenMedia from '../../Components/FullScreenMedia';
import { Supabase } from '../../Utils/supabase.config';
import { fetchPosts } from '../../services/postService';
import PostCard from '../../Components/PostCard';
import Loading from '../../Components/Loading';

import { getUserData } from '../../services/userService';

// Define navigation type for HomeStack
type HomeStackParamList = {
  Home: undefined;
  profile: { userId?: string };
  notifications: undefined;
  newPost: undefined;
  editProfile: undefined;
  search: undefined;
  postDetails: { postId: string };
  reels: undefined;
};

type NavigationProp = StackNavigationProp<HomeStackParamList>;

// Define a basic type for posts since database.types doesn't exist
interface Post {
  id: number;
  body?: string;
  file?: string;
  created_at: string;
  userId: string;
  user?: {
    id: string;
    name: string;
    image?: string;
  };
  postLikes?: any[];
  comments?: any[];
}

const Profile = () => {
  const { user, userData, setUserData, setAuth } = useAuth();
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute();
  const [posts, setPosts] = useState<Post[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [fullScreenVisible, setFullScreenVisible] = useState(false);
  const [postsLoading, setPostsLoading] = useState(false);

  const [profileUser, setProfileUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(10);
  const [communityLinkAllowedMap, setCommunityLinkAllowedMap] = useState<Record<string, boolean>>({});
  const [communityAdminMap, setCommunityAdminMap] = useState<Record<string, boolean>>({});

  // Get the userId from route params, default to logged-in user
  const targetUserId = (route.params as any)?.userId || user?.id;
  const isOwnProfile = targetUserId === user?.id;

  useEffect(() => {
    // Reset posts and pagination when target user changes
    setPosts([]);
    setHasMore(true);
    setLimit(10);
    loadProfileUser();
  }, [targetUserId]);

  useEffect(() => {
    if (profileUser && targetUserId) {
      loadPosts();
    }
  }, [profileUser, targetUserId]);

  // Refresh profile data when screen comes into focus
  useFocusEffect(
    React.useCallback(() => {
      // Only refresh if it's the user's own profile
      if (isOwnProfile) {
        loadProfileUser();
      }
    }, [isOwnProfile])
  );

  const loadProfileUser = async () => {
    setLoading(true);
    if (isOwnProfile) {
      // For own profile, refresh userData from server to get latest updates
      try {
        const res = await getUserData(user?.id);
        if (res.success) {
          setUserData(res.data);
          setProfileUser(res.data);
        } else {
          // Fallback to existing userData if refresh fails
          setProfileUser(userData);
        }
      } catch (error) {
        // Fallback to existing userData if refresh fails
        setProfileUser(userData);
      }
      setLoading(false);
    } else {
      try {
        const res = await getUserData(targetUserId);
        if (res.success) {
          setProfileUser(res.data);
        } else {
          Alert.alert('Error', 'Failed to load user profile');
        }
      } catch (error) {
        Alert.alert('Error', 'Failed to load user profile');
      }
      setLoading(false);
    }
  };

  const loadPosts = async (isLoadMore = false) => {
    if (!targetUserId) return;
    
    // Don't load more if we're already loading or if there's no more data
    if (isLoadMore && (!hasMore || postsLoading)) return;
    
    setPostsLoading(true);
    
    try {
      const currentLimit = isLoadMore ? limit + 10 : 10;
      const res = await fetchPosts(currentLimit, targetUserId);
      
      if (res.success && Array.isArray(res.data)) {
        const newPosts = res.data as Post[];

        // Update community link-allowed map from this batch
        setCommunityLinkAllowedMap(prev => {
          const next = { ...prev };
          newPosts.forEach((post: any) => {
            const communityId = String(post?.community_id || post?.communityId || '');
            const isAllowed = post?.community && typeof (post.community as any).is_linkallowed === 'boolean'
              ? Boolean((post.community as any).is_linkallowed)
              : undefined;
            if (communityId && typeof isAllowed === 'boolean' && next[communityId] === undefined) {
              next[communityId] = isAllowed;
            }
          });
          return next;
        });

        // Batch-check admin status for this user across all communities in this batch
        try {
          const communityIds = Array.from(
            new Set(
              newPosts
                .map((post: any) => String(post?.community_id || post?.communityId || ''))
                .filter(Boolean)
            )
          );

          const toCheck = communityIds.filter(id => communityAdminMap[id] === undefined);

          if (toCheck.length > 0) {
            const results = await Promise.all(
              toCheck.map(async (communityId) => {
                try {
                  const { data, error } = await Supabase.rpc('check_user_community_admin', {
                    user_uuid: targetUserId,
                    community_uuid: communityId,
                  });
                  if (error) {
                    return { communityId, isAdmin: false };
                  }
                  return { communityId, isAdmin: Boolean(data) };
                } catch {
                  return { communityId, isAdmin: false };
                }
              })
            );

            setCommunityAdminMap(prev => {
              const next = { ...prev };
              results.forEach(r => {
                next[r.communityId] = r.isAdmin;
              });
              return next;
            });
          }
        } catch {
          // Safe fallback: links remain plain text
        }
        
        if (isLoadMore) {
          // Append new posts for pagination
          setPosts(prev => [...prev, ...newPosts]);
        } else {
          // Replace posts for initial load
          setPosts(newPosts);
        }
        
        // Update limit
        setLimit(currentLimit);
        
        // Check if there are more posts
        // If we got fewer posts than requested, we've reached the end
        setHasMore(newPosts.length === 10);
        
      } else {
        if (!isLoadMore) {
          // Only show error for initial load, not for pagination
          Alert.alert('Error', res.msg || 'Failed to fetch posts');
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading posts:', error);
      if (!isLoadMore) {
        Alert.alert('Error', 'Failed to fetch posts');
      }
      setHasMore(false);
    } finally {
      setPostsLoading(false);
    }
  };

  const getPosts = () => {
    loadPosts(true);
  };

  const onLogout = async () => {
    setAuth(null);
    const { error } = await Supabase.auth.signOut();
    if (error) Alert.alert("Error Signing Out User", error.message);
  };

  const handleLogout = () => {
    Alert.alert('Confirm', 'Are you sure you want log out?', [
      { text: 'Cancel', onPress: () => {}, style: 'cancel' },
      { text: 'Logout', onPress: onLogout, style: 'destructive' }
    ]);
  };

  const handleAvatarPress = () => {
    if (profileUser?.image) {
      setFullScreenVisible(true);
    }
  };

  const closeFullScreen = () => {
    setFullScreenVisible(false);
  };

  // Show loading if profileUser is not yet loaded
  if (loading || !profileUser) {
    return (
      <ScreenWrapper bg="white">
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Loading />
          <Text style={{ marginTop: 20, color: theme.colors.textLight }}>Loading profile...</Text>
        </View>
      </ScreenWrapper>
    );
  }

  return (
    <ScreenWrapper bg="white">
      <FlatList
        data={posts}
        ListHeaderComponent={<UserHeader 
          user={profileUser} 
          handleLogout={handleLogout} 
          navigation={navigation} 
          isOwnProfile={isOwnProfile}
          onAvatarPress={handleAvatarPress}
        />}
        ListHeaderComponentStyle={{ marginBottom: 30 }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.listStyle}
        keyExtractor={(item) => item.id.toString()}
        renderItem={({ item }) => {
          const communityId = String((item as any)?.community_id || (item as any)?.communityId || '');
          const allowLinks =
            Boolean(communityId && communityLinkAllowedMap[communityId]) &&
            Boolean(communityId && communityAdminMap[communityId]);

          return (
            <PostCard
              item={item}
              currentUser={userData}
              showReportSpam={false}
              allowLinks={allowLinks}
            />
          );
        }}
        onEndReached={() => {
          getPosts();
        }}
        onEndReachedThreshold={0}
        ListFooterComponent={
          postsLoading ? (
            <View style={{ marginTop: posts.length === 0 ? 100 : 30 }}>
              <Loading />
            </View>
          ) : !hasMore && posts.length > 0 ? (
            <View style={{ marginVertical: 30 }}>
              <Text style={styles.noPosts}>No more posts</Text>
            </View>
          ) : posts.length === 0 ? (
            <View style={{ marginTop: 100, alignItems: 'center' }}>
              <Text style={styles.noPosts}>No posts yet</Text>
            </View>
          ) : null
        }
      />
      
      {/* Full Screen Media Modal */}
      <FullScreenMedia
        visible={fullScreenVisible}
        onClose={closeFullScreen}
        mediaUrl={profileUser?.image || ''}
        mediaType="image"
        title={profileUser?.name}
      />

    </ScreenWrapper>
  );
};

interface UserHeaderProps {
  user: any;
  handleLogout: () => void;
  navigation: NavigationProp;
  isOwnProfile: boolean;
  onAvatarPress: () => void;
}

const UserHeader: React.FC<UserHeaderProps> = ({ user, handleLogout, navigation, isOwnProfile, onAvatarPress }) => {
  return (
    <View style={{ flex: 1, backgroundColor: 'white' }}>
      <View>
        <Header title="Profile" mb={30} />
        {isOwnProfile && (
          <TouchableOpacity style={styles.logoutButton} onPress={handleLogout}>
            <Logout size={26} color={theme.colors.rose} />
          </TouchableOpacity>
        )}
      </View>

      <View style={styles.container}>
        <View style={{ gap: 15 }}>
          <View style={styles.avatarContainer}>
            <TouchableOpacity onPress={onAvatarPress} activeOpacity={0.8}>
              <Avatar
                uri={user?.image}
                size={hp(12)}
                rounded={theme.radius.xxl * 1.4}
              />
            </TouchableOpacity>
            {isOwnProfile && (
              <Pressable style={styles.editIcon} onPress={() => navigation.navigate('editProfile')}>
                <Edit strokeWidth={2.5} size={20} />
              </Pressable>
            )}
          </View>

          <View style={{ alignItems: 'center', gap: 4 }}>
            <Text style={styles.userName}>{user?.name}</Text>
            <Text style={styles.infoText}>{user?.address}</Text>
          </View>

          <View style={{ gap: 10 }}>            
            {user?.bio && <Text style={[styles.infoText]}>{user.bio}</Text>}            

          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  avatarContainer: {
    height: hp(12),
    width: hp(12),
    alignSelf: 'center',
  },
  editIcon: {
    position: 'absolute',
    bottom: 0,
    right: -12,
    padding: 7,
    borderRadius: 50,
    backgroundColor: 'white',
    shadowColor: theme.colors.textLight,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.4,
    shadowRadius: 5,
    elevation: 7,
  },
  userName: {
    fontSize: hp(3),
    fontWeight: '500',
    color: theme.colors.textDark,
  },
  info: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  infoText: {
    fontSize: hp(1.6),
    fontWeight: '500',
    color: theme.colors.textLight,
  },
  logoutButton: {
    position: 'absolute',
    right: 0,
    padding: 5,
    borderRadius: theme.radius.sm,
    backgroundColor: '#fee2e2',
  },

  listStyle: {
    paddingHorizontal: wp(4),
    paddingBottom: 30,
  },
  noPosts: {
    fontSize: hp(2),
    textAlign: 'center',
    color: theme.colors.text,
  },
});

export default Profile;
